import type { TFunction } from 'i18next';
import type { AppLocale } from '../i18n';
import type { PublicContentTagDto } from '../services/contentTagService';
import type { ExerciseFlowItemDto, QuizFlowItemDto } from '../services/contentService';
import {
  formatStructuredLessonPath,
  type LearningResumeEntry,
  type LearningResumePayload
} from './lastLearningPosition';
import {
  pickLocalized,
  pickLocalizedTagPublishedName,
  pickLocalizedTitle
} from './localizedContent';

export type KeepLearningLabelContext = {
  locale: AppLocale;
  tLearning: TFunction;
  publicTagsById: Map<number, PublicContentTagDto>;
  quizItemsByKey: Map<string, QuizFlowItemDto>;
  exerciseItemsByKey: Map<string, ExerciseFlowItemDto>;
};

export type KeepLearningEntryLabels = {
  title: string;
  subtitle?: string;
  lessonPathParts?: string[];
};

function flowItemKey(flowKind: string, id: number): string {
  return `${flowKind}-${id}`;
}

function pickEntryField(
  english: string | undefined,
  persian: string | undefined,
  locale: AppLocale,
  fallback?: string
): string {
  const picked = pickLocalized(english, persian, locale).trim();
  return picked || fallback?.trim() || '';
}

function quizItemLabels(
  item: QuizFlowItemDto,
  locale: AppLocale,
  tLearning: TFunction,
  progress?: { done?: number; total?: number }
): KeepLearningEntryLabels {
  const title = pickLocalizedTitle(item, locale);
  const subtitle =
    progress?.total != null && progress.total > 0
      ? tLearning('quizFlow.positionSummary', {
          done: progress.done ?? 0,
          total: progress.total
        })
      : undefined;
  return { title, subtitle };
}

function exerciseItemLabels(
  item: ExerciseFlowItemDto,
  locale: AppLocale,
  tLearning: TFunction,
  progress?: { done?: number; total?: number }
): KeepLearningEntryLabels {
  const title = pickLocalizedTitle(item, locale);
  const subtitle =
    progress?.total != null && progress.total > 0
      ? tLearning('exerciseFlow.positionSummary', {
          done: progress.done ?? 0,
          total: progress.total
        })
      : undefined;
  return { title, subtitle };
}

function lessonPathParts(
  payload: Extract<LearningResumePayload, { kind: 'lesson' }>,
  lessonTitle: string,
  locale: AppLocale
): string[] {
  return [
    pickEntryField(payload.courseTitle, payload.courseTitleFa, locale),
    pickEntryField(payload.levelTitle, payload.levelTitleFa, locale),
    lessonTitle
  ].filter((p) => Boolean(p.trim()));
}

export function resolveKeepLearningEntryLabels(
  entry: LearningResumeEntry,
  ctx: KeepLearningLabelContext
): KeepLearningEntryLabels {
  const { locale, tLearning, publicTagsById, quizItemsByKey, exerciseItemsByKey } = ctx;
  const payload = entry.payload;

  if (payload.kind === 'tag') {
    const tag = publicTagsById.get(payload.tagId);
    const title = tag
      ? pickLocalizedTagPublishedName(tag, locale)
      : pickEntryField(entry.title, entry.titleFa, locale, entry.title);
    const subtitle = pickEntryField(
      entry.subtitle,
      entry.subtitleFa ?? payload.itemTitleFa,
      locale,
      entry.subtitle
    );
    return subtitle ? { title, subtitle } : { title };
  }

  if (payload.kind === 'lesson') {
    const lessonTitle = pickEntryField(
      payload.lessonTitle ?? entry.title,
      payload.lessonTitleFa ?? entry.titleFa,
      locale,
      entry.title
    );
    const parts = lessonPathParts(payload, lessonTitle, locale);
    const path =
      parts.length > 0
        ? parts.join(' › ')
        : formatStructuredLessonPath(payload, lessonTitle) || entry.subtitle;
    return {
      title: lessonTitle,
      subtitle: path || undefined,
      lessonPathParts: parts.length > 0 ? parts : undefined
    };
  }

  if (payload.kind === 'quiz') {
    const progress = { done: payload.progressDone, total: payload.progressTotal };
    const subtitle =
      progress.total != null && progress.total > 0
        ? tLearning('quizFlow.positionSummary', { done: progress.done ?? 0, total: progress.total })
        : undefined;

    if (payload.quizId == null || !payload.flowKind) {
      return subtitle
        ? { title: tLearning('quizFlow.title'), subtitle }
        : { title: tLearning('quizFlow.title') };
    }

    const fromFlow = quizItemsByKey.get(flowItemKey(payload.flowKind, payload.quizId));
    if (fromFlow) return quizItemLabels(fromFlow, locale, tLearning, progress);

    const title =
      pickEntryField(payload.itemTitle ?? entry.title, payload.itemTitleFa ?? entry.titleFa, locale, entry.title) ||
      tLearning('quizFlow.title');
    return subtitle ? { title, subtitle } : { title };
  }

  if (payload.kind === 'exercise') {
    const progress = { done: payload.progressDone, total: payload.progressTotal };
    const subtitle =
      progress.total != null && progress.total > 0
        ? tLearning('exerciseFlow.positionSummary', {
            done: progress.done ?? 0,
            total: progress.total
          })
        : undefined;

    if (payload.exerciseId == null || !payload.flowKind) {
      return subtitle
        ? { title: tLearning('quickActionExercises'), subtitle }
        : { title: tLearning('quickActionExercises') };
    }

    const fromFlow = exerciseItemsByKey.get(flowItemKey(payload.flowKind, payload.exerciseId));
    if (fromFlow) return exerciseItemLabels(fromFlow, locale, tLearning, progress);

    const title =
      pickEntryField(
        payload.itemTitle ?? entry.title,
        payload.itemTitleFa ?? entry.titleFa,
        locale,
        entry.title
      ) || tLearning('quickActionExercises');
    return subtitle ? { title, subtitle } : { title };
  }

  if (payload.kind === 'book') {
    const title = pickEntryField(
      payload.bookTitle ?? entry.title,
      payload.bookTitleFa ?? entry.titleFa,
      locale,
      entry.title
    );
    const pageCount = payload.pageCount;
    const subtitle =
      pageCount != null && pageCount > 0
        ? tLearning('quiz.pageOf', { current: payload.pageIndex + 1, total: pageCount })
        : entry.subtitle;
    return subtitle ? { title, subtitle } : { title };
  }

  return {
    title: pickEntryField(entry.title, entry.titleFa, locale, entry.title),
    subtitle: pickEntryField(entry.subtitle, entry.subtitleFa, locale, entry.subtitle) || undefined
  };
}
